import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { useDebouncedState } from 'hooks/useDebouncedState';
import { activeCustomWorklistIDsState } from '../state';
import { SEARCH_DEBOUNCE_MS } from '../constants';
import { logger } from 'modules/logger';

type WorklistSearchMap = Record<string, string>;

type WorklistSearchContextBag = {
  searchForActiveWorklist: string;
  debouncedSearchForActiveWorklist: string;
  setSearchForActiveWorklist: (value: string) => void;
  clearSearchForActiveWorklist: () => void;
};

const WorklistSearchContext = createContext<WorklistSearchContextBag | null>(null);

export const WorklistSearchContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => {
  const activeCustomWorklistIDs = useRecoilValue(activeCustomWorklistIDsState);

  const [searches, debouncedSearches, setSearches] = useDebouncedState<WorklistSearchMap>(
    {},
    SEARCH_DEBOUNCE_MS
  );

  const searchForActiveWorklist = useMemo(() => {
    if (activeCustomWorklistIDs.length === 0) {
      return '';
    }
    return searches[activeCustomWorklistIDs[0]] ?? '';
  }, [activeCustomWorklistIDs, searches]);

  const debouncedSearchForActiveWorklist = useMemo(() => {
    if (activeCustomWorklistIDs.length === 0) {
      return '';
    }
    return debouncedSearches[activeCustomWorklistIDs[0]] ?? '';
  }, [activeCustomWorklistIDs, debouncedSearches]);

  const setSearchForActiveWorklist = useCallback(
    (value: string) => {
      if (activeCustomWorklistIDs.length === 0) {
        logger.warn('[WorklistSearchContext] Cannot set search - no active worklist IDs');
        return;
      }

      const worklistID = activeCustomWorklistIDs[0];
      logger.debug('[WorklistSearchContext] Setting search for active worklist', {
        worklistID,
        value,
        activeCustomWorklistIDs,
        previousValue: searches[worklistID],
      });

      setSearches((currentSearches) => ({
        ...currentSearches,
        [worklistID]: value,
      }));
    },
    [activeCustomWorklistIDs, searches, setSearches]
  );

  const clearSearchForActiveWorklist = useCallback(() => {
    if (activeCustomWorklistIDs.length === 0) {
      logger.warn('[WorklistSearchContext] Cannot clear search - no active worklist IDs');
      return;
    }

    const worklistID = activeCustomWorklistIDs[0];
    logger.debug('[WorklistSearchContext] Clearing search for active worklist', {
      worklistID,
      activeCustomWorklistIDs,
      previousValue: searches[worklistID],
    });

    setSearches((currentSearches) => {
      const { [worklistID]: _, ...rest } = currentSearches;
      return rest;
    });
  }, [activeCustomWorklistIDs, searches, setSearches]);

  const contextValue = useMemo<WorklistSearchContextBag>(
    () => ({
      searchForActiveWorklist,
      debouncedSearchForActiveWorklist,
      setSearchForActiveWorklist,
      clearSearchForActiveWorklist,
    }),
    [
      searchForActiveWorklist,
      debouncedSearchForActiveWorklist,
      setSearchForActiveWorklist,
      clearSearchForActiveWorklist,
    ]
  );

  return (
    <WorklistSearchContext.Provider value={contextValue}>{children}</WorklistSearchContext.Provider>
  );
};

export const useWorklistSearchContext = (): WorklistSearchContextBag => {
  const context = useContext(WorklistSearchContext);

  if (!context) {
    throw new Error(
      'useWorklistSearchContext must be used within a WorklistSearchContextProvider.'
    );
  }

  return context;
};
